/**
 * Complete Storage Reset Utility
 *
 * Provides functions to reset all quantum storage when assets are encrypted
 * with keys that are no longer accessible.
 */

import { resetQuantumStorage } from '@/services/EncryptedAssetStorage';
import { resetKeyStorage } from '@/services/KeyStorage';

/**
 * Complete reset of all quantum storage
 * Clears both encrypted assets and stored keys
 */
export async function resetAllQuantumStorage(): Promise<void> {
  console.log('🔄 Starting complete quantum storage reset...');

  try {
    // Clear all encrypted assets
    console.log('1️⃣ Clearing encrypted assets...');
    await resetQuantumStorage();

    // Clear all stored keys
    console.log('2️⃣ Clearing stored keys...');
    await resetKeyStorage();

    console.log('✅ Complete quantum storage reset successful!');
    console.log('💡 You can now encrypt assets with fresh keys.');

    return Promise.resolve();
  } catch (error) {
    console.error('❌ Reset failed:', error);
    throw error;
  }
}

/**
 * Debug function to inspect all storage before reset
 */
export async function debugAllStorage(): Promise<void> {
  console.log('🔍 === COMPLETE STORAGE DEBUG ===');

  try {
    // Import debug functions dynamically to avoid circular deps
    const { debugQuantumStorage } = await import('@/services/EncryptedAssetStorage');
    const { debugKeyStorage } = await import('@/services/KeyStorage');

    console.log('📦 Encrypted Assets Storage:');
    await debugQuantumStorage();

    console.log('🔑 Key Storage:');
    await debugKeyStorage();

    console.log('🔍 === END COMPLETE STORAGE DEBUG ===');
  } catch (error) {
    console.error('Debug failed:', error);
  }
}

// Make functions available globally for browser console
if (typeof window !== 'undefined') {
  (window as any).resetAllQuantumStorage = resetAllQuantumStorage;
  (window as any).debugAllStorage = debugAllStorage;

  // Add helpful console messages
  console.log('🛠️  Quantum Storage Utilities Available:');
  console.log('   resetAllQuantumStorage() - Clear all assets and keys');
  console.log('   debugAllStorage() - Inspect all storage');
  console.log('   resetQuantumStorage() - Clear only encrypted assets');
  console.log('   resetKeyStorage() - Clear only stored keys');
  console.log('   debugQuantumStorage() - Debug asset storage');
  console.log('   debugKeyStorage() - Debug key storage');
}